import { Box, styled, SxProps, Theme, Tooltip, useTheme } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';

import useCopyClipboard from '../../../hooks/useCopyClipboard';

export interface IAdditionalAction {
  icon: React.ReactNode;
  value: string;
  tooltip?: string;
  onClick?: (e: React.MouseEvent) => void;
  toastLabel?: string;
}

export interface ICopyableLinkProps {
  children: React.ReactNode;
  value: string;
  onTextClick?: () => void;
  textTooltip?: string;
  copyTooltip?: string;
  textStyle?: SxProps<Theme>;
  additionalActions?: IAdditionalAction[];
  toastLabel?: string;
}

const Container = styled(Box)(() => ({
  position: 'relative',
  marginTop: -5,
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  '&:hover': {
    '& .MuiSvgIcon-root': {
      opacity: 1,
      transform: 'translateX(0)',
    },
  },
}));

const TextContainer = styled(Box)(({ theme }) => ({
  display: 'inline',
  cursor: 'pointer',
  fontSize: 16,
  fontWeight: 400,
  transition: 'all 0.2s ease',
  '&:hover': {
    color: theme.wl?.portal_accent_color,
    textDecoration: 'underline',
  },
}));

const CopyIconWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isTruncated' && prop !== 'noMargin',
})<{ isTruncated?: boolean; noMargin?: boolean }>(({ isTruncated = false, noMargin = false }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  verticalAlign: 'middle',
  ...(isTruncated
    ? {
        position: 'absolute',
        right: 0,
        bottom: 0,
        marginLeft: 0,
        paddingLeft: 4,
      }
    : {
        position: 'static',
        marginLeft: noMargin ? 0 : 4,
      }),
}));

export const CopyableLink: React.FC<ICopyableLinkProps> = ({
  children,
  value,
  onTextClick,
  textTooltip,
  copyTooltip = 'Copy',
  textStyle,
  additionalActions = [],
  toastLabel,
}) => {
  const { wl } = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { setCopied } = useCopyClipboard({ successDuration: 1000, toastDuration: 1000, label: toastLabel });

  const handleCopy = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCopied(value);
    },
    [setCopied, value],
  );

  const handleTextClick = useCallback(
    (e: React.MouseEvent) => {
      if (onTextClick) {
        e.stopPropagation();
        onTextClick();
      }
    },
    [onTextClick],
  );

  const handleAdditionalAction = useCallback(
    (action: IAdditionalAction) => (e: React.MouseEvent) => {
      e.stopPropagation();
      if (action.onClick) {
        action.onClick(e);
      }
      setCopied(action.value, action.toastLabel);
    },
    [setCopied],
  );

  const iconStyle = {
    width: 16,
    height: 16,
    marginLeft: '3px',
    color: wl?.portal_accent_color,
    transition: 'all 0.25s ease',
    opacity: isHovered ? 1 : 0,
    transform: !isHovered ? 'translateX(-100%)' : 'unset',
    cursor: 'pointer',
  };

  const copyIconElement = (
    <Tooltip title={copyTooltip} arrow placement="top">
      <CopyIconWrapper isTruncated={isTruncated}>
        <ContentCopyOutlinedIcon
          onClick={handleCopy}
          sx={{
            ...iconStyle,
          }}
        />
      </CopyIconWrapper>
    </Tooltip>
  );

  const textClickHandler = onTextClick ? handleTextClick : undefined;

  const additionalActionStyle = {
    ...iconStyle,
    display: 'flex',
    alignItems: 'center',
    '& .MuiSvgIcon-root': {
      width: 16,
      height: 16,
    },
  };

  // Detect text truncation
  useEffect(() => {
    const checkTruncation = () => {
      if (containerRef.current) {
        const { scrollHeight, clientHeight } = containerRef.current;
        setIsTruncated(scrollHeight > clientHeight);
      }
    };

    checkTruncation();

    // Re-check on window resize
    const resizeObserver = new ResizeObserver(checkTruncation);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [children, value]);

  return (
    <Container
      ref={containerRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={textStyle}
    >
      {textTooltip && onTextClick ? (
        <Tooltip title={textTooltip} arrow placement="top">
          <TextContainer onClick={textClickHandler}>{children}</TextContainer>
        </Tooltip>
      ) : (
        <TextContainer onClick={textClickHandler}>{children}</TextContainer>
      )}
      {copyIconElement}
      {additionalActions.map((action) => (
        <Tooltip key={action.tooltip} title={action.tooltip || 'Copy'} arrow placement="top">
          <CopyIconWrapper noMargin>
            <Box onClick={handleAdditionalAction(action)} sx={additionalActionStyle}>
              {action.icon}
            </Box>
          </CopyIconWrapper>
        </Tooltip>
      ))}
    </Container>
  );
};
